/**
 * Right Sidebar Context
 * 
 * Provides a way for pages to set their sidebar content
 * while keeping the sidebar rendered at the layout level.
 * 
 * Uses separate contexts for reading (sections) and writing (setSections)
 * to prevent re-render loops.
 */

import { createContext, useContext, useState, ReactNode, useCallback, useRef } from 'react';

export interface SidebarSection {
    id: string;
    icon: ReactNode;  // Icon as rendered JSX element
    title: string;    // Title for tooltip when collapsed
    content: ReactNode;  // The full detail component including its own title/header
    defaultOpen?: boolean;  // Whether section is expanded by default
    badge?: string | number;  // Optional badge to show on icon
}

// Separate context for reading sections (will cause re-renders)
interface SectionsContextValue {
    sections: SidebarSection[];
}

// Separate context for setting sections (stable, won't cause re-renders)
interface SetSectionsContextValue {
    setSections: (sections: SidebarSection[]) => void;
    clearSections: () => void;
}

const SectionsContext = createContext<SectionsContextValue | null>(null);
const SetSectionsContext = createContext<SetSectionsContextValue | null>(null);

export function RightSidebarProvider({ children }: { children: ReactNode }) {
    const [sections, setSectionsState] = useState<SidebarSection[]>([]);
    const currentIdsRef = useRef<string>('');

    const setSections = useCallback((newSections: SidebarSection[]) => {
        // Always update state - the sections array may have new content even if IDs are same
        const newIds = newSections.map(s => s.id).join(',');
        currentIdsRef.current = newIds;
        setSectionsState(newSections);
    }, []);

    const clearSections = useCallback(() => {
        currentIdsRef.current = '';
        setSectionsState([]);
    }, []);

    // Create a stable object that holds the current setter functions
    const settersRef = useRef<SetSectionsContextValue | null>(null);
    if (!settersRef.current) {
        settersRef.current = { setSections, clearSections };
    }
    // Keep the ref updated with the latest callbacks
    settersRef.current.setSections = setSections;
    settersRef.current.clearSections = clearSections;

    return (
        <SetSectionsContext.Provider value={settersRef.current}>
            <SectionsContext.Provider value={{ sections }}>
                {children}
            </SectionsContext.Provider>
        </SetSectionsContext.Provider>
    );
}

// Hook for reading sections (used by GlobalRightSidebar)
export function useRightSidebarSections() {
    const context = useContext(SectionsContext);
    if (!context) {
        throw new Error('useRightSidebarSections must be used within RightSidebarProvider');
    }
    return context.sections;
}

// Hook for setting sections (used by pages via useRightSidebar)
export function useRightSidebarSetters() {
    const context = useContext(SetSectionsContext);
    if (!context) {
        throw new Error('useRightSidebarSetters must be used within RightSidebarProvider');
    }
    return context;
}

// Legacy hook for backward compatibility - uses both contexts
export function useRightSidebarContext() {
    const sections = useRightSidebarSections();
    const { setSections, clearSections } = useRightSidebarSetters();
    return { sections, setSections, clearSections };
}
